import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { renderRich } from "@/components/note/shared";
import { toast } from "sonner";
import { Send, RotateCcw, Sparkles, X, MessageSquareText } from "lucide-react";

/**
 * "Chat with this note" — sliding sidebar panel.
 * Streams gpt-5.6-terra responses token-by-token via the backend SSE endpoint.
 */
export default function ChatWithNote({ noteId, open, onClose }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // [{role, text}]
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState(""); // live assistant tokens
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Load history on open
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get(`/notes/${noteId}/chat/history`);
        setSessionId(data.session_id);
        setMessages(
          (data.messages || []).map((m) => ({ role: m.role, text: m.text }))
        );
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    return () => abortRef.current?.abort();
  }, [open, noteId]);

  // Auto-scroll on new tokens
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, draft]);

  const resetChat = async () => {
    try {
      const { data } = await api.post(`/notes/${noteId}/chat/reset`);
      setSessionId(data.session_id);
      setMessages([]);
      setDraft("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setDraft("");
    setMessages((m) => [...m, { role: "user", text }]);

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("access_token");
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${BACKEND_URL}/api/notes/${noteId}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text, session_id: sessionId }),
        signal: controller.signal,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let assembled = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop() ?? "";
        for (const chunk of chunks) {
          const evLine = chunk.match(/^event:\s*(.+)$/m);
          const dataLine = chunk.match(/^data:\s*(.+)$/m);
          if (!evLine || !dataLine) continue;
          const ev = evLine[1].trim();
          try {
            const parsed = JSON.parse(dataLine[1]);
            if (ev === "session") setSessionId(parsed.session_id);
            else if (ev === "delta") {
              assembled += parsed.t || "";
              setDraft(assembled);
            } else if (ev === "done") {
              setMessages((m) => [...m, { role: "assistant", text: assembled }]);
              setDraft("");
            } else if (ev === "error") {
              throw new Error(parsed.detail || "Stream error");
            }
          } catch {
            /* ignore malformed chunk */
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(e.message || "Chat failed");
        setMessages((m) => [...m, { role: "assistant", text: "⚠️ Could not reach the model. Try again." }]);
      }
    } finally {
      setStreaming(false);
      setDraft("");
      abortRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="chat-backdrop"
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />
      {/* Sidebar */}
      <aside
        data-testid="chat-sidebar"
        className="fixed top-0 right-0 h-dvh w-full sm:w-[440px] bg-white border-l-2 border-black z-50 flex flex-col shadow-[-6px_0_0_0_#0A0A0A]"
      >
        <header className="flex items-center justify-between gap-2 px-4 py-3 border-b-2 border-black bg-[#F0F9FF]">
          <div className="flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-[#3B82F6]" />
            <h3 className="font-heading font-bold text-lg">Chat with this note</h3>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="chat-reset-btn"
              onClick={resetChat}
              disabled={streaming}
              title="Start a new chat"
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-40"
            >
              <RotateCcw className="w-3 h-3" /> New
            </button>
            <button
              data-testid="chat-close-btn"
              onClick={onClose}
              className="inline-flex items-center justify-center w-7 h-7 border-2 border-black hover:bg-black hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div
          ref={scrollRef}
          data-testid="chat-messages"
          className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        >
          {messages.length === 0 && !draft && (
            <div className="border-2 border-dashed border-black p-5 bg-[#FEF9C3]">
              <p className="font-heading font-bold text-lg mb-1">Ask anything about this note</p>
              <p className="text-sm text-zinc-700">
                I'll only answer from what's inside this note. Try:
              </p>
              <ul className="mt-3 space-y-2">
                {[
                  "Give me the TL;DR in 3 lines",
                  "Build 3 quick MCQs from this note",
                  "Explain the hardest concept like I'm 12",
                ].map((p) => (
                  <li key={p}>
                    <button
                      data-testid={`chat-suggestion-${p.length}`}
                      onClick={() => setInput(p)}
                      className="w-full text-left font-mono text-xs px-3 py-2 border border-black bg-white hover:bg-black hover:text-white transition-colors"
                    >
                      <Sparkles className="w-3 h-3 inline mr-2" />
                      {p}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {messages.map((m, i) => (
            <Message key={i} role={m.role} text={m.text} />
          ))}
          {draft && <Message role="assistant" text={draft} streaming />}
        </div>

        <footer className="px-4 py-3 border-t-2 border-black bg-white">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this note…"
              rows={2}
              className="flex-1 border-2 border-black px-3 py-2 font-mono text-sm resize-none focus:outline-none focus:ring-0 focus:border-[#3B82F6]"
              disabled={streaming}
            />
            <button
              data-testid="chat-send-btn"
              onClick={sendMessage}
              disabled={streaming || !input.trim()}
              className="inline-flex items-center justify-center h-[52px] w-[52px] bg-[#3B82F6] text-white btn-brutal disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Grounded on this note · gpt-5.6-terra
          </p>
        </footer>
      </aside>
    </>
  );
}

function Message({ role, text, streaming = false }) {
  const isUser = role === "user";
  return (
    <div
      data-testid={isUser ? "chat-msg-user" : "chat-msg-assistant"}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[88%] px-3 py-2 border-2 border-black ${
          isUser
            ? "bg-[#DBEAFE] rounded-tr-none"
            : "bg-white rounded-tl-none"
        } ${streaming ? "animate-pulse" : ""}`}
      >
        <p className="text-sm whitespace-pre-wrap leading-relaxed">
          {renderChatText(text)}
          {streaming && <span className="inline-block w-1.5 h-4 bg-black ml-0.5 align-middle animate-pulse" />}
        </p>
      </div>
    </div>
  );
}

/**
 * Chat-only text renderer. Strips stray markdown-bold markers the model
 * occasionally emits ("**word**") and converts them into real <strong>
 * spans. Then runs through renderRich so KaTeX ($...$) still works.
 */
function renderChatText(text) {
  if (!text) return text;
  const parts = String(text).split(/(\*\*[^*\n]+?\*\*)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*\n]+?\*\*$/.test(part)) {
      return (
        <strong key={i} className="font-semibold">
          {renderRich(part.slice(2, -2))}
        </strong>
      );
    }
    // Also drop any orphan asterisks like "**word" or "word**" that didn't pair.
    return renderRich(part.replace(/\*\*/g, ""));
  });
}
